import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Pagination,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { Done as DoneIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { crm, fmtDateTime, fromNow, inr } from '../../../api/crm';
import { useSales } from '../../../context/SalesContext';

const LIMIT = 10;

// usePanel pages one of the owner-scoped panel endpoints.
const usePanel = (path, enabled, extraQuery) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async (p = page) => {
    if (!enabled) return;
    setLoading(true);
    try {
      const data = await crm(path, { query: { page: p, limit: LIMIT, ...(extraQuery || {}) } });
      setRows(Array.isArray(data?.data) ? data.data : []);
      setTotal(data?.total || 0);
    } catch (e) {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled, page]);
  useEffect(() => { load(page); }, [load, page]);
  return { rows, total, page, setPage, loading, reload: () => load(page) };
};

const NumberLink = ({ number }) => (
  <Link href={`/sales/profile/${number}`} target="_blank" rel="noopener" underline="hover" fontWeight={600}>{number}</Link>
);

const Line = ({ left, right, sub }) => (
  <Box sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>{left}</Box>
      <Box sx={{ flexShrink: 0, textAlign: 'right' }}>{right}</Box>
    </Box>
    {sub && <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-word' }}>{sub}</Typography>}
  </Box>
);

const planOf = (r) => r.plan_name || r.plan_assets || '';

// useReminders pings 15 minutes before one of the viewer's follow-ups is due.
const useReminders = (followups) => {
  const seen = useRef(new Set());
  const audio = useRef(null);
  useEffect(() => {
    const unlock = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx && !audio.current) audio.current = new Ctx();
        if (audio.current && audio.current.state === 'suspended') audio.current.resume();
      } catch (e) {
        audio.current = null;
      }
    };
    document.addEventListener('click', unlock, { once: true });
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission();
    } catch (e) {
      // not supported on this browser
    }
    return () => document.removeEventListener('click', unlock);
  }, []);

  useEffect(() => {
    const check = () => {
      const now = Date.now();
      followups.forEach((fu) => {
        const at = new Date(fu.remind_at).getTime();
        if (!(at > now && at <= now + 15 * 60000) || seen.current.has(fu.id)) return;
        seen.current.add(fu.id);
        const text = `Follow-up for ${fu.client_number} in ${Math.max(1, Math.round((at - now) / 60000))} minutes`;
        try {
          if (audio.current) {
            const osc = audio.current.createOscillator();
            const gain = audio.current.createGain();
            osc.connect(gain);
            gain.connect(audio.current.destination);
            osc.frequency.setValueAtTime(800, audio.current.currentTime);
            gain.gain.setValueAtTime(0.1, audio.current.currentTime);
            osc.start();
            osc.stop(audio.current.currentTime + 0.2);
          }
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') new Notification('Upcoming follow-up', { body: text });
        } catch (e) {
          // a reminder that cannot beep is still listed in the panel
        }
      });
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, [followups]);
};

// LeadPanels: the viewer's follow-ups plus the owner-scoped payment and callback feeds.
const LeadPanels = () => {
  const sales = useSales();
  const canPayments = sales.can('payment_panels');
  const canCallbacks = sales.can('callbacks_panel');
  // Pending follow-ups from the last two weeks onwards, oldest first, so overdue ones
  // stay on top until they are marked done.
  const since = useRef(new Date(Date.now() - 14 * 86400e3).toISOString());
  const followups = usePanel('followups/upcoming', true, { since: since.current });
  const failed = usePanel('failed-payments', canPayments);
  const paid = usePanel('successful-payments', canPayments);
  const callbacks = usePanel('callbacks', canCallbacks);
  useReminders(followups.rows);

  const tabs = [
    { key: 'followups', label: 'My follow-ups', panel: followups },
    canCallbacks && { key: 'callbacks', label: 'Callbacks', panel: callbacks },
    canPayments && { key: 'failed', label: 'Failed payments', panel: failed },
    canPayments && { key: 'paid', label: 'Payments', panel: paid },
  ].filter(Boolean);
  const [tab, setTab] = useState('followups');
  const active = tabs.find((t) => t.key === tab) || tabs[0];

  const markDone = async (id) => {
    try {
      await crm(`followups/${id}`, { method: 'PATCH', body: { status: 'done' } });
      followups.reload();
    } catch (e) {
      // stays in the list; the user can retry
    }
  };

  const body = () => {
    const { rows } = active.panel;
    if (rows.length === 0) {
      return <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{active.panel.loading ? 'Loading…' : 'Nothing here.'}</Typography>;
    }
    switch (active.key) {
      case 'followups':
        return rows.map((fu) => (
          <Line
            key={fu.id}
            left={<><NumberLink number={fu.client_number} /> <Typography component="span" variant="body2">— {fu.note}</Typography></>}
            right={<IconButton size="small" color="success" title="Mark done" onClick={() => markDone(fu.id)}><DoneIcon fontSize="small" /></IconButton>}
            sub={<Box component="span" sx={{ color: new Date(fu.remind_at) < new Date() ? 'error.main' : 'text.secondary' }}>{fmtDateTime(fu.remind_at)} · {fromNow(fu.remind_at)}</Box>}
          />
        ));
      case 'callbacks':
        return rows.map((cb, i) => (
          <Line
            key={`${cb.number}-${i}`}
            left={<NumberLink number={cb.number} />}
            right={<Typography variant="caption" color="text.secondary">{fromNow(cb.created_at)}</Typography>}
            sub={[String(cb.source || '').replace('callback_card_', '').replace(/_/g, ' '), cb.message].filter(Boolean).join(' · ')}
          />
        ));
      case 'failed':
        return rows.map((r, i) => (
          <Line
            key={`${r.number}-${i}`}
            left={<><NumberLink number={r.number} /> <Chip size="small" color="error" label={inr(r.amount)} sx={{ height: 20, ml: 0.5 }} /></>}
            right={<Typography variant="caption" color="text.secondary">{fromNow(r.created_at)}</Typography>}
            sub={planOf(r)}
          />
        ));
      default:
        return rows.map((r, i) => (
          <Line
            key={`${r.number}-${i}`}
            left={<><NumberLink number={r.number} /> <Chip size="small" color="success" label={inr(r.amount)} sx={{ height: 20, ml: 0.5 }} /></>}
            right={<Typography variant="caption" color="text.secondary">{fromNow(r.paid_at || r.created_at)}</Typography>}
            sub={planOf(r)}
          />
        ));
    }
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={active.key} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ flexGrow: 1, minHeight: 40, '& .MuiTab-root': { textTransform: 'none', minHeight: 40 } }}>
          {tabs.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={(
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {t.label}
                  {t.panel.total > 0 && <Chip size="small" color={t.key === 'paid' ? 'success' : t.key === 'failed' ? 'error' : 'primary'} label={t.panel.total} sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }} />}
                </Box>
              )}
            />
          ))}
        </Tabs>
        <IconButton size="small" onClick={active.panel.reload} sx={{ mr: 1 }}>{active.panel.loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}</IconButton>
      </Box>
      <Box sx={{ px: 1.5, py: 0.5 }}>
        {active.key === 'failed' && <Typography variant="caption" color="text.secondary">Checkouts that did not complete. Shown 4 minutes after the attempt; links you sent and auto-debits are not listed.</Typography>}
        {body()}
        {active.panel.total > LIMIT && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <Pagination size="small" count={Math.ceil(active.panel.total / LIMIT)} page={active.panel.page} onChange={(_, p) => active.panel.setPage(p)} siblingCount={0} />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default LeadPanels;
